import { Grid, Stack, Typography } from '@mui/material'
import { KFieldLabel } from 'components/core/KFieldLabel'
import { useCurrentStore, useOrdering } from 'hooks'
import { useEffect, useMemo } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { buildOrderTypeNotes } from 'services'
import { SubmitOrderSectionProps } from './SubmitOrderSectionProps'
import { OrderTypeSelect } from './OrderTypeSelect'
import { Order, OrderType } from 'models'
import { OrderAsapLaterSelect } from './OrderAsapLaterSelect'
import { OrderInfoDeliveryAddressInput } from './OrderInfoDeliveryAddressInput'
import { OrderInfoTimeInput } from './OrderInfoTimeInput'
import { calculateItemPriceWhenChangeOrderType } from 'services/OrderingService'

interface OrderInfoProps {
  allowChangingOrderType?: boolean
  submitForm: SubmitOrderSectionProps
}

export const OrderInfo = ({
  submitForm: { form },
  allowChangingOrderType = true,
}: OrderInfoProps) => {
  const { t } = useTranslation()
  const { order, isAsapEnabled, saveOrderProps } = useOrdering()
  const {
    store,
    isOpening,
    isOrderTypeAllowedWhenClosed,
    isAsapAllowedForOrderType,
  } = useCurrentStore()

  useEffect(() => {
    if (!order) return

    let orderUpdates: Partial<Order> = {}

    // Handle order type validation when store is closed
    if (!isOpening && order.order_type) {
      const isOrderTypeAllowed = isOrderTypeAllowedWhenClosed(order.order_type)

      if (!isOrderTypeAllowed) {
        let newOrderType: OrderType = 'pickup'
        if (store?.accept_pickup === false && store?.accept_dinein !== false) {
          newOrderType = 'dinein'
        }
        orderUpdates.order_type = newOrderType
        orderUpdates.is_asap = false
      }
    }

    // Handle ASAP validation
    const currentOrderType = orderUpdates.order_type || order.order_type
    if (
      order.is_asap !== false &&
      (!isAsapAllowedForOrderType(currentOrderType) || isAsapEnabled === false)
    ) {
      orderUpdates.is_asap = false
    }

    // Apply updates if any changes are needed
    if (Object.keys(orderUpdates).length > 0) {
      saveOrderProps(orderUpdates)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAsapEnabled, isOpening, order?.order_type, order?.is_asap])

  const orderType = order?.order_type

  const notes = useMemo(() => {
    if (!store || !order?.order_type) return []
    return buildOrderTypeNotes(store, order?.order_type)
  }, [store, order?.order_type])

  return (
    <Stack sx={{ gap: 2 }}>
      <Grid container spacing={2.5}>
        {allowChangingOrderType && (
          <Grid
            item
            xs={12}
            sm={6}
            container
            direction="column"
            sx={{ gap: 1, justifyContent: 'flex-end' }}
          >
            <OrderTypeSelect
              value={orderType}
              onChange={(value) => {
                if (order && store) {
                  const updatedOrder = calculateItemPriceWhenChangeOrderType(
                    order,
                    store,
                    value,
                  )
                  saveOrderProps(updatedOrder)
                }
              }}
            />
          </Grid>
        )}
        <Grid
          item
          xs={12}
          sm={6}
          container
          direction="column"
          sx={{ gap: 1, justifyContent: 'flex-end' }}
        >
          <KFieldLabel>{t('When would you like to order?')}</KFieldLabel>
          <OrderAsapLaterSelect
            value={order?.is_asap}
            onChange={(value) => saveOrderProps({ is_asap: value })}
          />
        </Grid>
      </Grid>

      <OrderInfoTimeInput form={form} />
      <OrderInfoDeliveryAddressInput form={form} />

      <Stack spacing={0.5} sx={{ flexGrow: 1 }}>
        <Typography variant="h4" color="error" mb={0.5}>
          {t('Important notes:')}
        </Typography>
        {notes.map(([note, params], index) => (
          <Typography key={index} variant="body2" color="secondary">
            <Trans defaults={note} values={params} />
          </Typography>
        ))}
      </Stack>
    </Stack>
  )
}
